import React from 'react';
import { formatUnits, parseUnits } from 'viem';
import { Loader2, ArrowDown } from 'lucide-react';
import { colors, spacing, typography, borders } from '../../../../tokens';
import { formatTokenAmount, hasSufficientBalance, isValidAmount } from '../utils/formatters';

interface BridgeFormProps {
  // Form state
  amount: string;
  onAmountChange: (value: string) => void;
  onMaxClick: () => void;

  // Contract data
  balance: bigint | undefined;
  allowance: bigint | undefined;
  decimals: number;
  isBalanceLoading: boolean;

  // Fee data
  nativeFee: bigint;
  lzTokenFee: bigint;
  isFetchingFee: boolean;
  isFeeQuoted: boolean;
  feeError: Error | null;
  onQuoteFees: () => void;

  // Actions
  onApprove: () => void;
  onBridge: () => void;

  // Loading states
  isApprovePending: boolean;
  isApproveLoading: boolean;
  isBridgePending: boolean;
  isBridgeLoading: boolean;

  // Network check
  isOnCorrectNetwork: boolean;
}

/**
 * Main bridge form component
 * Handles amount input, approval, fee quoting, and bridge execution
 */
export const BridgeForm: React.FC<BridgeFormProps> = ({
  amount,
  onAmountChange,
  onMaxClick,
  balance,
  allowance,
  decimals,
  isBalanceLoading,
  nativeFee,
  lzTokenFee,
  isFetchingFee,
  isFeeQuoted,
  feeError,
  onQuoteFees,
  onApprove,
  onBridge,
  isApprovePending,
  isApproveLoading,
  isBridgePending,
  isBridgeLoading,
  isOnCorrectNetwork,
}) => {
  // Parse amount to bigint
  const amountBigInt = amount && parseFloat(amount) > 0 ? parseUnits(amount, decimals) : 0n;

  // Check if approval is needed
  const needsApproval = allowance !== undefined && amountBigInt > allowance && amountBigInt > 0n;

  // Check if user has sufficient balance
  const insufficientBalance = !hasSufficientBalance(balance, amountBigInt);

  // Check if amount is valid
  const validAmount = isValidAmount(amount);

  // Determine button states (all require correct network)
  const canApprove = isOnCorrectNetwork && needsApproval && validAmount && !insufficientBalance;
  const canQuote = isOnCorrectNetwork && !needsApproval && validAmount && !insufficientBalance && !isFeeQuoted;
  const canBridge = isOnCorrectNetwork && !needsApproval && validAmount && !insufficientBalance && isFeeQuoted && nativeFee > 0n;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.lg,
        padding: spacing.xl,
        backgroundColor: colors.background.secondary.default,
        borderRadius: borders.component.card.radius,
        border: `1px solid ${colors.border.default.default}`,
      }}
    >
      {/* Amount Input */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        <label
          style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: colors.text.secondary.default,
          }}
        >
          Amount
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            placeholder="0.00"
            style={{
              width: '100%',
              padding: spacing.md,
              paddingRight: '80px',
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.medium,
              backgroundColor: colors.background.primary.default,
              color: colors.text.primary.default,
              border: `1px solid ${colors.border.default.default}`,
              borderRadius: borders.component.input.radius,
              outline: 'none',
            }}
          />
          <button
            onClick={onMaxClick}
            disabled={isBalanceLoading || !balance}
            style={{
              position: 'absolute',
              right: spacing.sm,
              top: '50%',
              transform: 'translateY(-50%)',
              padding: `${spacing.xs} ${spacing.sm}`,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.semibold,
              color: colors.brand.primary.base,
              backgroundColor: 'transparent',
              border: `1px solid ${colors.brand.primary.base}`,
              borderRadius: borders.radius.sm,
              cursor: 'pointer',
            }}
          >
            MAX
          </button>
        </div>
        <div
          style={{
            fontSize: typography.fontSize.xs,
            color: colors.text.muted.default,
          }}
        >
          Balance: {isBalanceLoading ? '...' : formatTokenAmount(balance, decimals)} FAR
        </div>
        {insufficientBalance && validAmount && (
          <div
            style={{
              fontSize: typography.fontSize.xs,
              color: colors.semantic.error,
            }}
          >
            Insufficient balance
          </div>
        )}
      </div>

      {/* Bridge Direction Indicator */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: `${spacing.sm} 0`,
        }}
      >
        <ArrowDown size={24} color={colors.text.secondary.default} />
      </div>

      {/* Fee Display */}
      {isFeeQuoted && nativeFee > 0n && (
        <div
          style={{
            padding: spacing.md,
            backgroundColor: colors.background.primary.default,
            borderRadius: borders.radius.md,
            border: `1px solid ${colors.border.default.default}`,
          }}
        >
          <div
            style={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: colors.text.secondary.default,
              marginBottom: spacing.sm,
            }}
          >
            Bridge Fees
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: typography.fontSize.sm,
              color: colors.text.primary.default,
            }}
          >
            <span>Native Fee:</span>
            <span>{formatUnits(nativeFee, 18)} {/* Native token */}</span>
          </div>
          {lzTokenFee > 0n && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: typography.fontSize.sm,
                color: colors.text.primary.default,
                marginTop: spacing.xs,
              }}
            >
              <span>LZ Token Fee:</span>
              <span>{formatUnits(lzTokenFee, 18)}</span>
            </div>
          )}
        </div>
      )}

      {/* Fee Error Display */}
      {feeError && (
        <div
          style={{
            padding: spacing.md,
            backgroundColor: colors.semantic.error,
            color: colors.text.primary.default,
            borderRadius: borders.radius.md,
            fontSize: typography.fontSize.sm,
          }}
        >
          <strong>Error getting fees:</strong> {feeError.message || 'Unknown error occurred'}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        {needsApproval && (
          <button
            onClick={onApprove}
            disabled={!canApprove || isApprovePending || isApproveLoading}
            style={{
              padding: spacing.md,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              backgroundColor: canApprove ? colors.brand.primary.base : colors.background.primary.default,
              border: `1px solid ${canApprove ? colors.brand.primary.base : colors.border.default.default}`,
              borderRadius: borders.component.button.radius,
              cursor: canApprove ? 'pointer' : 'not-allowed',
              opacity: canApprove ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
            }}
          >
            {(isApprovePending || isApproveLoading) && <Loader2 size={16} className="animate-spin" />}
            {isApproveLoading ? 'Approving...' : isApprovePending ? 'Confirm in wallet...' : 'Approve FAR'}
          </button>
        )}

        {!needsApproval && !isFeeQuoted && (
          <button
            onClick={onQuoteFees}
            disabled={!canQuote || isFetchingFee}
            style={{
              padding: spacing.md,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              backgroundColor: canQuote ? colors.brand.blue.base : colors.background.primary.default,
              border: `1px solid ${canQuote ? colors.brand.blue.base : colors.border.default.default}`,
              borderRadius: borders.component.button.radius,
              cursor: canQuote ? 'pointer' : 'not-allowed',
              opacity: canQuote ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
            }}
          >
            {isFetchingFee && <Loader2 size={16} className="animate-spin" />}
            {isFetchingFee ? 'Getting fees...' : 'Get Bridge Fees'}
          </button>
        )}

        {!needsApproval && isFeeQuoted && (
          <button
            onClick={onBridge}
            disabled={!canBridge || isBridgePending || isBridgeLoading}
            style={{
              padding: spacing.md,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              backgroundColor: canBridge ? colors.semantic.success : colors.background.primary.default,
              border: `1px solid ${canBridge ? colors.semantic.success : colors.border.default.default}`,
              borderRadius: borders.component.button.radius,
              cursor: canBridge ? 'pointer' : 'not-allowed',
              opacity: canBridge ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
            }}
          >
            {(isBridgePending || isBridgeLoading) && <Loader2 size={16} className="animate-spin" />}
            {isBridgeLoading
              ? 'Bridging...'
              : isBridgePending
              ? 'Confirm in wallet...'
              : 'Bridge to BSC'}
          </button>
        )}
      </div>
    </div>
  );
};
